import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Divider,
  Form,
  Input,
  Spin,
  Tag,
  Typography,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useAppSelector } from "../../../../../app/hooks";
import { selectCurrentBusiness } from "../../../businessesSlice";
import {
  getPosConnectionsFromServer,
  getPosProvidersFromServer,
  updatePosConnectionServer,
} from "../../../businessesApi";
import {
  PosConnection,
  PosConnectionStatus,
  PosCredentialFieldDef,
  PosProviderInfo,
} from "../../../../../interfaces/posProvider.interface";

const STATUS_COLORS: Record<PosConnectionStatus, string> = {
  [PosConnectionStatus.Connected]: "green",
  [PosConnectionStatus.Pending]: "orange",
  [PosConnectionStatus.BadCredentials]: "red",
  [PosConnectionStatus.Deleted]: "default",
};

// Optional supplementary hints per provider (not part of credentialFields).
const PROVIDER_NOTES: Record<string, JSX.Element> = {
  mpluskassa: (
    <>
      The ident and secret can be found in the{" "}
      <Typography.Link
        href="https://developers.mpluskassa.nl/partners/klearly/?_login=17e0fb44e2"
        target="_blank"
        rel="noopener noreferrer"
      >
        MPlusKassa partner portal
      </Typography.Link>
      .
    </>
  ),
};

type ResultMessage = { type: "success" | "warning" | "error"; text: string };

function CredentialForm({
  connection,
  businessId,
  fields,
  note,
  onStatusChange,
}: {
  connection: PosConnection;
  businessId: string;
  fields: PosCredentialFieldDef[];
  note?: JSX.Element;
  onStatusChange: (connectionId: string, status: PosConnectionStatus) => void;
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ResultMessage | null>(null);

  const handleSubmit = async (values: Record<string, unknown>) => {
    setLoading(true);
    setResult(null);
    try {
      const credentials: Record<string, string> = {};
      for (const field of fields) {
        const value = values[field.key];
        if (value !== undefined && value !== null && value !== "") {
          credentials[field.key] = String(value);
        }
      }
      const data = await updatePosConnectionServer({
        posConnectionId: connection._id,
        businessId,
        credentials,
      });
      const status = data.status as PosConnectionStatus;
      if (status === PosConnectionStatus.Connected) {
        setResult({
          type: "success",
          text: "Connection activated successfully.",
        });
        onStatusChange(connection._id, status);
      } else if (status === PosConnectionStatus.BadCredentials) {
        setResult({
          type: "warning",
          text: "Credentials were rejected by the POS. Please check the values.",
        });
        onStatusChange(connection._id, status);
      } else if (status === PosConnectionStatus.Pending) {
        setResult({ type: "warning", text: "Connection is pending." });
        onStatusChange(connection._id, status);
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to update connection";
      setResult({ type: "error", text: msg });
    } finally {
      setLoading(false);
    }
  };

  if (fields.length === 0) {
    return (
      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
        This provider has no editable credentials.
      </Typography.Text>
    );
  }

  return (
    <Form form={form} onFinish={handleSubmit} layout="vertical">
      {result && (
        <Alert
          type={result.type}
          message={result.text}
          showIcon
          closable
          onClose={() => setResult(null)}
          style={{ marginBottom: 12 }}
        />
      )}
      {note && (
        <Typography.Text
          type="secondary"
          style={{ display: "block", marginBottom: 12, fontSize: 13 }}
        >
          {note}
        </Typography.Text>
      )}
      {fields.map((field) => (
        <Form.Item
          key={field.key}
          name={field.key}
          label={field.label}
          extra={field.description}
          rules={
            field.required
              ? [{ required: true, message: `${field.label} is required` }]
              : []
          }
        >
          {field.type === "password" ? (
            <Input.Password autoComplete="new-password" />
          ) : (
            <Input
              type={field.type === "number" ? "number" : "text"}
              autoComplete="new-password"
            />
          )}
        </Form.Item>
      ))}
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading}>
          Update Credentials
        </Button>
      </Form.Item>
    </Form>
  );
}

export default function PosConnectEditor() {
  const currentBusiness = useAppSelector(selectCurrentBusiness);
  const [connections, setConnections] = useState<PosConnection[]>([]);
  const [providers, setProviders] = useState<PosProviderInfo[]>([]);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const providerById = useMemo(
    () => Object.fromEntries(providers.map((p) => [p.id, p])),
    [providers]
  );

  const fetchConnections = async () => {
    setLoadingConnections(true);
    setFetchError(null);
    try {
      const [connectionsData, providersData] = await Promise.all([
        getPosConnectionsFromServer(currentBusiness._id),
        getPosProvidersFromServer(),
      ]);
      setConnections((connectionsData.connections || []) as PosConnection[]);
      setProviders(providersData.providers || []);
    } catch (err: any) {
      setFetchError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load POS connections"
      );
    } finally {
      setLoadingConnections(false);
    }
  };

  useEffect(() => {
    if (currentBusiness._id) fetchConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBusiness._id]);

  const handleStatusChange = (
    connectionId: string,
    status: PosConnectionStatus
  ) => {
    setConnections((prev) =>
      prev.map((c) => (c._id === connectionId ? { ...c, status } : c))
    );
  };

  return (
    <div style={{ padding: "0 8px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <span style={{ fontWeight: 500 }}>POS Connections</span>
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          onClick={fetchConnections}
          size="small"
          loading={loadingConnections}
        >
          Refresh
        </Button>
      </div>

      {loadingConnections && (
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <Spin />
        </div>
      )}

      {!loadingConnections && fetchError && (
        <Alert type="error" message={fetchError} showIcon />
      )}

      {!loadingConnections && !fetchError && connections.length === 0 && (
        <Alert
          type="info"
          message="No POS connections found for this business."
          showIcon
        />
      )}

      {!loadingConnections &&
        connections.map((connection) => {
          const provider = providerById[connection.posProvider];
          return (
            <Card
              key={connection._id}
              style={{ marginBottom: 16 }}
              title={
                <span>
                  {provider?.name || connection.posProvider}
                  <Tag
                    color={STATUS_COLORS[connection.status]}
                    style={{ marginLeft: 12 }}
                  >
                    {connection.status}
                  </Tag>
                </span>
              }
            >
              <Divider orientation="left" plain style={{ fontSize: 13 }}>
                Update Credentials
              </Divider>
              <CredentialForm
                connection={connection}
                businessId={currentBusiness._id}
                fields={provider?.credentialFields || []}
                note={PROVIDER_NOTES[connection.posProvider]}
                onStatusChange={handleStatusChange}
              />
            </Card>
          );
        })}
    </div>
  );
}
